import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    BookmarkPlus,
    Check,
    Globe,
    Loader2,
    Lock,
    Music,
    Pause,
    Pencil,
    Play,
    Search,
    SearchX,
    Trash2,
    UserRound,
} from "lucide-react";

import { useTherapist } from "../../../../context/useTherapist";
import Button from "../../../../shared/ui/button/Button";
import {
    useCenterMeditationTracks,
    useCopyMeditationTrack,
    useDeleteMeditationTrack,
    useMeditationTracks,
    usePublicMeditationTracks,
    useRenameMeditationTrack,
    useSetTrackVisibility,
    useToggleTrackLike,
} from "../../application/useMeditationTracks";
import {
    MeditationTrack,
    MeditationTrackFilters,
    TRACKS_PAGE_SIZE,
    formatDuration,
} from "../../domain/types";
import CopyCount from "./CopyCount";
import { ActiveFilterChips, FilterChip, FiltersToggle } from "../../../../shared/ui/form/FilterControls";
import LikeHeart from "./LikeHeart";
import MeditationPager from "./MeditationPager";
import MeditationTrackUploader from "./MeditationTrackUploader";
import AudioPlayerBar from "./AudioPlayerBar";
import { useTrackPlayer } from "./useTrackPlayer";

/**
 * The therapist's music, on a shelf of its own.
 *
 * Uploading from inside the recording studio was enough to make one
 * meditation, and wrong for building a practice: the same piece goes under a
 * dozen of them and under a script read live, and a therapist should not have
 * to open a recorder — or find the file again — to reach music they already
 * gave us.
 *
 * So the library is a place, listed beside their meditations, where a track is
 * added once, heard, renamed and removed. Everywhere a bed is chosen reads
 * from here.
 */
type Props = {
    /**
     * The therapist's own shelf, their centre's, or the public catalogue.
     * Everybody in the centre plays the centre's; only its administrators add
     * to it or change it. The public one is played by all and changed by
     * nobody but each track's owner, from their own shelf.
     */
    scope?: "own" | "center" | "public";
    /** Whether they may put music on this shelf. */
    canUpload?: boolean;
    /** Where an empty shelf points: the public catalogue, from their own. */
    onExplore?: () => void;
    /** Where an empty public catalogue points: their own shelf, to share. */
    onShareYours?: () => void;
};

export default function MeditationTrackLibrary({
    scope = "own",
    canUpload = true,
    onExplore,
    onShareYours,
}: Props) {
    const { t } = useTranslation();
    const { therapist } = useTherapist();
    // Who wrote it, in a catalogue others fill: the next piece a therapist wants
    // is often by the person whose last one worked. The name is always in
    // view; who shared it opens under "Filtros", as the meditations' rarer
    // filters do. The music that works for most can go first.
    const [filters, setFilters] = useState<MeditationTrackFilters>({});
    const [filtersOpen, setFiltersOpen] = useState(false);
    // Ten at a time, as the meditations are; back to the first page whenever
    // what is listed changes.
    const [page, setPage] = useState(1);
    const changeFilters = (next: MeditationTrackFilters) => {
        setFilters(next);
        setPage(1);
    };
    const own = useMeditationTracks();
    const center = useCenterMeditationTracks();
    const publicCatalogue = usePublicMeditationTracks(
        scope === "public",
        {
            search: filters.search?.trim() || undefined,
            author: filters.author?.trim() || undefined,
            order: filters.order,
        },
        page
    );
    const chips: FilterChip[] = filters.author?.trim()
        ? [{ key: "author", label: t("meditation.byAuthor", { author: filters.author.trim() }) }]
        : [];
    const narrowed = Boolean(filters.search?.trim() || chips.length);
    const { data: loaded, isLoading } =
        scope === "center" ? center : scope === "public" ? publicCatalogue : own;
    // The whole of their own shelf and their centre's is loaded and paged here;
    // the public catalogue comes a page at a time.
    const totalPages =
        scope === "public" || !loaded
            ? undefined
            : Math.max(1, Math.ceil(loaded.length / TRACKS_PAGE_SIZE));
    const currentPage = totalPages ? Math.min(page, totalPages) : page;
    const tracks =
        scope === "public" || !loaded
            ? loaded
            : loaded.slice((currentPage - 1) * TRACKS_PAGE_SIZE, currentPage * TRACKS_PAGE_SIZE);
    const hasNext = scope === "public" ? publicCatalogue.hasNext : currentPage < (totalPages ?? 1);
    // In the public catalogue nothing is changed, not even one's own: that is
    // done from one's own shelf, where the music is.
    const manages = (track: { can_manage?: boolean }) =>
        scope !== "public" && track.can_manage !== false;
    const anyManageable = (tracks ?? []).some(manages);
    const renameTrack = useRenameMeditationTrack();
    const deleteTrack = useDeleteMeditationTrack();
    const setVisibility = useSetTrackVisibility();
    const toggleLike = useToggleTrackLike();
    const copyTrack = useCopyMeditationTrack();
    const [visibilityError, setVisibilityError] = useState<string | null>(null);
    /*
     * The track being shared, and the name it will be shared under. Asked in
     * place rather than in a dialog: two things to say — that the music is
     * theirs to hand on, and whose to call it — and the track stays in view.
     */
    const [sharing, setSharing] = useState<string | null>(null);
    const [shareAuthor, setShareAuthor] = useState("");

    const changeVisibility = async (id: string, isPublic: boolean, authorName?: string) => {
        setVisibilityError(null);
        try {
            await setVisibility.mutateAsync({ id, isPublic, authorName });
            setSharing(null);
        } catch {
            // Said, not swallowed: otherwise the icon just stays as it was and
            // the therapist cannot tell a refusal from a click that missed.
            setVisibilityError(t("meditation.tracks.visibilityFailed"));
        }
    };

    const toggleShared = (track: MeditationTrack) => {
        if (track.is_public) {
            void changeVisibility(track.id, false);
            return;
        }
        // The name it was last shared under, or theirs as a start.
        setShareAuthor(
            track.author_name ??
                [therapist?.first_name, therapist?.last_name].filter(Boolean).join(" ")
        );
        setSharing(track.id);
    };

    const addToMine = (id: string) => {
        setVisibilityError(null);
        copyTrack.mutateAsync(id).catch(() =>
            setVisibilityError(t("meditation.tracks.addFailed"))
        );
    };

    const [editing, setEditing] = useState<string | null>(null);
    const [draftName, setDraftName] = useState("");
    const player = useTrackPlayer();
    const isPlaying = (id: string) =>
        player.playback?.track.id === id && !player.playback.paused;

    const rename = async (id: string) => {
        const name = draftName.trim();
        if (name) await renameTrack.mutateAsync({ id, name });
        setEditing(null);
    };

    return (
        <div className="flex flex-col gap-5">
            {canUpload && scope !== "public" && (
                <MeditationTrackUploader forCenter={scope === "center"} />
            )}

            {scope === "public" && (
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-end gap-3">
                        <label className="flex min-w-0 flex-1 basis-60 flex-col gap-1 text-xs font-medium text-content-tertiary">
                            {t("meditation.tracks.search")}
                            <span className="relative">
                                <Search
                                    size={15}
                                    aria-hidden="true"
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-tertiary"
                                />
                                <input
                                    type="search"
                                    className="field-base field-default h-11 w-full py-2 pl-9 pr-3 text-sm"
                                    placeholder={t("meditation.tracks.searchPlaceholder")}
                                    value={filters.search ?? ""}
                                    onChange={(e) =>
                                        changeFilters({ ...filters, search: e.target.value || undefined })
                                    }
                                />
                            </span>
                        </label>
                        <label className="flex w-full flex-col gap-1 text-xs font-medium text-content-tertiary sm:w-48">
                            {t("meditation.filters.order")}
                            <select
                                className="field-base field-default h-11 w-full appearance-none px-4 text-sm"
                                value={filters.order ?? "recent"}
                                onChange={(e) =>
                                    changeFilters({
                                        ...filters,
                                        order: e.target.value === "likes" ? "likes" : undefined,
                                    })
                                }
                            >
                                <option value="recent">
                                    {t("meditation.filters.orderRecent")}
                                </option>
                                <option value="likes">{t("meditation.filters.orderLikes")}</option>
                            </select>
                        </label>
                        <FiltersToggle
                            expanded={filtersOpen}
                            count={chips.length}
                            onToggle={() => setFiltersOpen((value) => !value)}
                        />
                    </div>
                    {filtersOpen && (
                        <div className="grid gap-3 rounded-xl border border-border-subtle bg-surface-raised p-3 sm:grid-cols-2 lg:grid-cols-3">
                            <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                                {t("meditation.filters.author")}
                                <span className="relative">
                                    <UserRound
                                        size={15}
                                        aria-hidden="true"
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-tertiary"
                                    />
                                    <input
                                        type="search"
                                        className="field-base field-default h-11 w-full py-2 pl-9 pr-3 text-sm"
                                        placeholder={t("meditation.filters.authorPlaceholder")}
                                        value={filters.author ?? ""}
                                        onChange={(e) =>
                                            changeFilters({
                                                ...filters,
                                                author: e.target.value || undefined,
                                            })
                                        }
                                    />
                                </span>
                            </label>
                        </div>
                    )}
                    <ActiveFilterChips
                        chips={chips}
                        onRemove={(key) => changeFilters({ ...filters, [key]: undefined })}
                        onClear={() => changeFilters({ search: filters.search, order: filters.order })}
                    />
                </div>
            )}

            {isLoading ? (
                <p className="flex items-center gap-2 text-sm text-content-secondary">
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                    {t("common.loading")}
                </p>
            ) : !tracks || (tracks.length === 0 && currentPage === 1) ? (
                // As the meditations do: an empty shelf says where to go next.
                <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border-subtle px-6 py-10 text-center">
                    <span className="flex size-12 items-center justify-center rounded-full bg-accent-tint text-accent-on-tint">
                        {narrowed ? (
                            <SearchX size={20} aria-hidden="true" />
                        ) : (
                            <Music size={20} aria-hidden="true" />
                        )}
                    </span>
                    <p className="max-w-md text-sm text-content-secondary">
                        {t(
                            narrowed
                                ? "meditation.tracks.emptyFiltered"
                                : scope === "center"
                                  ? "meditation.tracks.centerEmpty"
                                  : scope === "public"
                                    ? "meditation.tracks.publicEmpty"
                                    : "meditation.tracks.empty"
                        )}
                    </p>
                    {narrowed ? (
                        <Button size="sm" variant="outline" onClick={() => changeFilters({ order: filters.order })}>
                            {t("meditation.filters.clear")}
                        </Button>
                    ) : scope === "public" && onShareYours ? (
                        <Button size="sm" variant="outline" onClick={onShareYours}>
                            {t("meditation.tracks.shareYours")}
                        </Button>
                    ) : scope === "own" && onExplore ? (
                        <Button
                            size="sm"
                            variant="outline"
                            startIcon={<Globe size={15} />}
                            onClick={onExplore}
                        >
                            {t("meditation.tracks.explorePublic")}
                        </Button>
                    ) : null}
                </div>
            ) : (
                <>
                    {/* The public catalogue is never counted whole; it says its page. */}
                    <p
                        id={`track-results-${scope}`}
                        className="scroll-mt-4 text-sm text-content-tertiary"
                        aria-live="polite"
                    >
                        {scope === "public"
                            ? t("meditation.pager.page", { page: currentPage })
                            : t("meditation.tracks.results", { count: loaded?.length ?? 0 })}
                    </p>
                    <ul className="flex flex-col gap-2">
                        {tracks.map((track) => (
                            <li
                                key={track.id}
                                className="flex min-w-0 flex-wrap items-center gap-3 rounded-2xl border border-border-subtle bg-surface-raised px-4 py-3"
                            >
                                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-500 dark:bg-brand-500/10">
                                    <Music size={16} aria-hidden="true" />
                                </span>

                                {editing === track.id ? (
                                    <>
                                        <input
                                            className="field-base field-default h-11 min-w-0 flex-1 px-3 text-sm"
                                            value={draftName}
                                            autoFocus
                                            aria-label={t("meditation.tracks.rename")}
                                            onChange={(e) => setDraftName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") void rename(track.id);
                                                if (e.key === "Escape") setEditing(null);
                                            }}
                                        />
                                        <Button
                                            size="sm"
                                            startIcon={<Check size={15} />}
                                            loading={renameTrack.isPending}
                                            onClick={() => void rename(track.id)}
                                        >
                                            {t("common.save")}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-medium text-content-primary">
                                                {track.name}
                                            </span>
                                            <span className="block text-xs text-content-tertiary">
                                                {track.duration_seconds
                                                    ? formatDuration(track.duration_seconds)
                                                    : null}
                                                {scope === "own" && track.is_public && (
                                                    <>
                                                        {track.duration_seconds ? " · " : null}
                                                        {t("meditation.tracks.shared")}
                                                    </>
                                                )}
                                                {/* What removing it would touch, said before anyone tries. */}
                                                {scope === "own" && Boolean(track.meditation_count) && (
                                                    <>
                                                        {track.duration_seconds || track.is_public
                                                            ? " · "
                                                            : null}
                                                        {t("meditation.tracks.usedIn", {
                                                            count: track.meditation_count,
                                                        })}
                                                    </>
                                                )}
                                                {scope === "public" && track.can_manage && (
                                                    <>
                                                        {track.duration_seconds ? " · " : null}
                                                        {t("meditation.tracks.yours")}
                                                    </>
                                                )}
                                                {track.author_name &&
                                                    (scope === "public" || !track.can_manage) && (
                                                        <>
                                                            {track.duration_seconds ||
                                                            (scope === "public" && track.can_manage)
                                                                ? " · "
                                                                : null}
                                                            {t("meditation.byAuthor", {
                                                                author: track.author_name,
                                                            })}
                                                        </>
                                                    )}
                                            </span>
                                        </span>

                                        {track.is_public && (
                                            <span className="flex shrink-0 items-center gap-2">
                                                <LikeHeart
                                                    liked={Boolean(track.liked_by_me)}
                                                    count={track.like_count ?? 0}
                                                    onToggle={() => toggleLike.mutate(track)}
                                                    pending={toggleLike.isPending && toggleLike.variables?.id === track.id}
                                                    // Their own, on their shelf or found in the catalogue.
                                                    readOnly={scope === "own" || Boolean(track.can_manage)}
                                                />
                                                <CopyCount count={track.copy_count} />
                                            </span>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => player.toggle(track)}
                                            aria-label={t(
                                                isPlaying(track.id)
                                                    ? "meditation.stopPreview"
                                                    : "meditation.previewTrack",
                                                { track: track.name }
                                            )}
                                            className="flex size-11 shrink-0 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-sunken"
                                        >
                                            {isPlaying(track.id) ? (
                                                <Pause size={16} />
                                            ) : (
                                                <Play size={16} />
                                            )}
                                        </button>
                                        {scope === "public" && !track.can_manage && (
                                            track.my_copy_id ? (
                                                <span className="flex min-h-9 shrink-0 items-center gap-1 px-2 text-xs font-medium text-content-secondary">
                                                    <Check size={14} aria-hidden="true" />
                                                    {t("meditation.tracks.inMine")}
                                                </span>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    startIcon={<BookmarkPlus size={15} />}
                                                    loading={
                                                        copyTrack.isPending &&
                                                        copyTrack.variables === track.id
                                                    }
                                                    onClick={() => addToMine(track.id)}
                                                >
                                                    {t("meditation.tracks.addToMine")}
                                                </Button>
                                            )
                                        )}
                                        {/* Music added from the catalogue is its author's to share. */}
                                        {scope === "own" && track.can_manage !== false && !track.is_copy && (
                                            <button
                                                type="button"
                                                onClick={() => toggleShared(track)}
                                                disabled={setVisibility.isPending}
                                                aria-pressed={Boolean(track.is_public)}
                                                aria-label={t(
                                                    track.is_public
                                                        ? "meditation.tracks.unshare"
                                                        : "meditation.tracks.share",
                                                    { track: track.name }
                                                )}
                                                title={t(
                                                    track.is_public
                                                        ? "meditation.tracks.unshare"
                                                        : "meditation.tracks.share",
                                                    { track: track.name }
                                                )}
                                                className={`flex size-11 shrink-0 items-center justify-center rounded-lg hover:bg-surface-sunken ${
                                                    track.is_public
                                                        ? "text-accent-text"
                                                        : "text-content-secondary"
                                                }`}
                                            >
                                                {track.is_public ? <Globe size={15} /> : <Lock size={15} />}
                                            </button>
                                        )}
                                        {manages(track) && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setEditing(track.id);
                                                        setDraftName(track.name);
                                                    }}
                                                    aria-label={t("meditation.tracks.rename")}
                                                    className="flex size-11 shrink-0 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-sunken"
                                                >
                                                    <Pencil size={15} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (player.playback?.track.id === track.id) player.stop();
                                                        setVisibilityError(null);
                                                        deleteTrack.mutateAsync(track.id).catch(() =>
                                                            setVisibilityError(t("meditation.tracks.removeFailed"))
                                                        );
                                                    }}
                                                    aria-label={t("meditation.tracks.remove", {
                                                        track: track.name,
                                                    })}
                                                    className="flex size-11 shrink-0 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-sunken"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}

                                {sharing === track.id && (
                                    <form
                                        className="flex basis-full flex-col gap-3 border-t border-border-subtle pt-3"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            void changeVisibility(track.id, true, shareAuthor.trim());
                                        }}
                                    >
                                        <p className="text-sm text-content-secondary">
                                            {t("meditation.tracks.shareConfirm")}
                                        </p>
                                        <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                                            {t("meditation.tracks.shareAuthor")}
                                            <input
                                                className="field-base field-default h-11 w-full px-3 text-sm"
                                                value={shareAuthor}
                                                autoFocus
                                                onChange={(e) => setShareAuthor(e.target.value)}
                                            />
                                        </label>
                                        <span className="flex flex-wrap justify-end gap-2">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setSharing(null)}
                                            >
                                                {t("common.cancel")}
                                            </Button>
                                            <Button
                                                size="sm"
                                                type="submit"
                                                startIcon={<Globe size={15} />}
                                                disabled={!shareAuthor.trim()}
                                                loading={setVisibility.isPending}
                                            >
                                                {t("meditation.tracks.shareAction")}
                                            </Button>
                                        </span>
                                    </form>
                                )}
                            </li>
                        ))}
                    </ul>
                    <MeditationPager
                        page={currentPage}
                        totalPages={totalPages}
                        hasNext={hasNext}
                        label={t("meditation.tracks.pagerLabel")}
                        onChange={(next) => {
                            setPage(next);
                            // The next ten start at the top, not where the last ten ended.
                            document
                                .getElementById(`track-results-${scope}`)
                                ?.scrollIntoView?.({ block: "start" });
                        }}
                    />
                </>
            )}

            {visibilityError && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {visibilityError}
                </p>
            )}

            {/*
              * Said here rather than only at the delete button: a therapist
              * about to tidy up wants to know it is safe before they start.
              */}
            {anyManageable && (
                <p className="text-xs text-content-tertiary">
                    {t("meditation.tracks.removalNotice")}
                </p>
            )}

            {player.playback && (
                <AudioPlayerBar
                    title={player.playback.track.name}
                    author={player.playback.track.author_name}
                    paused={player.playback.paused}
                    position={player.playback.position}
                    duration={player.playback.duration}
                    onToggle={() => player.playback && player.toggle(player.playback.track)}
                    onSeek={player.seek}
                    onClose={player.stop}
                />
            )}
        </div>
    );
}
